/**
 * Web Speech API wrapper. Picks a UK English voice if the browser exposes one.
 * Rate-limited globally so the city doesn't sound like a riot.
 */
class ScouseSpeakerImpl {
  private voice: SpeechSynthesisVoice | null = null;
  private lastSpokenAt = 0;
  private minGapMs = 600;

  constructor() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices();
      // Prefer Liverpool/UK male if available, else any en-GB, else default English.
      this.voice =
        voices.find(v => /en-GB/i.test(v.lang) && /(daniel|oliver|male)/i.test(v.name)) ??
        voices.find(v => /en-GB/i.test(v.lang)) ??
        voices.find(v => /^en/i.test(v.lang)) ??
        null;
    };
    pick();
    window.speechSynthesis.onvoiceschanged = pick;
  }

  /**
   * Speak a phrase if not in cooldown.
   * Defaults aim for a Scouse lilt: noticeably higher pitch + slightly faster rate.
   * Combined with phonetic respelling in PED_BARKS.say, the standard en-GB voice
   * lands somewhere in the Mersey ballpark.
   */
  speak(text: string, opts: { pitch?: number; rate?: number; volume?: number } = {}) {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const now = performance.now();
    if (now - this.lastSpokenAt < this.minGapMs) return;
    this.lastSpokenAt = now;

    const u = new SpeechSynthesisUtterance(text);
    if (this.voice) u.voice = this.voice;
    u.lang = 'en-GB';
    u.pitch = opts.pitch ?? (1.7 + Math.random() * 0.3);  // 1.7–2.0 — full nasal lilt
    u.rate  = opts.rate  ?? (1.15 + Math.random() * 0.25); // a touch quick
    u.volume = opts.volume ?? 0.75;
    window.speechSynthesis.speak(u);
  }
}

export const ScouseSpeaker = new ScouseSpeakerImpl();
